/**
 * VUKZ Investor Narrative Data
 * Narrative layer on top of financial source of truth.
 * Numbers come from @/data/finance — this file adds STORY.
 */

export const INVESTOR_THESIS = {
  oneLiner: 'Plataforma B2B de inteligência esportiva com AI e Visão Computacional para clubes, treinadores e federações no Brasil e LATAM.',
  
  problem: [
    'Clubes e treinadores no Brasil usam planilhas, WhatsApp e zero dados de vídeo para gestão de performance',
    '95% dos clubes brasileiros não têm software de analytics',
    'Análise biomecânica por vídeo custa R$ 5.000+/mês com empresas internacionais',
    'Dados esportivos fragmentados entre múltiplas fontes sem padronização',
    'Profissionais de performance (nutricionistas, fisioterapeutas, biomecânicos) não têm ferramentas integradas',
  ],

  solution: [
    'Pipeline AI com 14 agentes que automatiza ingestão, validação e persistência de dados esportivos',
    'TrackVision: visão computacional multi-esporte (atletismo, futebol, ginástica, vôlei, surf, crossfit)',
    'SwimVision: análise biomecânica de natação por vídeo (braçada, virada, saída)',
    'Plataforma SaaS B2B com planos de R$ 199 a R$ 7.990/mês',
    'AI Agents vendáveis no Microsoft Marketplace como produto standalone',
  ],

  whyNow: [
    'Custos de GPU caíram 80% nos últimos 2 anos (viabiliza CV em escala)',
    'Microsoft for Startups validou a tese com $250K em créditos Azure',
    'Brasil é potência olímpica emergente com investimento crescente em performance',
    'Copilot extensibility permite vender AI agents como produto no marketplace Microsoft',
    'Pós-pandemia acelerou digitalização de clubes e academias',
    'Lei do Esporte e editais de inovação em esporte criaram orçamento em federações',
  ],

  unfairAdvantages: [
    '12+ meses de desenvolvimento em visão computacional esportiva',
    '35+ AI agents funcionais em produção (raridade absoluta no mercado)',
    'Identity resolution proprietário (fuzzy ≥92%)',
    'Schema maduro com 250+ migrations e 40+ tabelas',
    'Parceria Microsoft (Azure credits + marketplace + co-sell potential)',
    'Único player combinando CV + AI agents + multi-sport + governança no Brasil',
  ],
};

export const MARKET_NARRATIVE = {
  title: 'Mercado de Sports Analytics & AI',
  
  globalContext: {
    marketSize: '$8.4B (2024) → $22B (2030)',
    cagr: '17.5%',
    drivers: ['Aumento de investimento em performance', 'Democratização de AI/CV', 'Expansão de dados esportivos'],
  },

  brazilContext: {
    clubs: '~50.000 clubes esportivos formais',
    coaches: '~30.000 treinadores com CREF',
    federations: '~200 federações estaduais + confederações',
    gyms: '~35.000 academias (CrossFit, funcional, musculação)',
    athletes: '~2M atletas federados',
    digitalPenetration: '<5% com software de analytics',
    opportunity: 'Mercado sub-penetrado com alta disposição a pagar quando entrega valor mensurável',
  },

  segments: [
    {
      name: 'Treinadores de Alto Rendimento',
      size: '~5.000 no Brasil',
      pain: 'Precisam de dados objetivos para tomada de decisão',
      willingness: 'Alta — R$ 300-600/mês facilmente justificável',
      entry: 'VUKZ Coach Pro/Performance',
    },
    {
      name: 'Clubes de Natação',
      size: '~800 clubes com programa competitivo',
      pain: 'PDFs de resultados manuais, zero análise biomecânica',
      willingness: 'Média-Alta — R$ 1.500-5.000/mês para clube médio',
      entry: 'SwimVision + Club Performance',
    },
    {
      name: 'Clubes Multi-esporte',
      size: '~3.000 com 2+ modalidades',
      pain: 'Cada esporte com ferramenta diferente ou nenhuma',
      willingness: 'Alta — R$ 2.000-8.000/mês consolidando ferramentas',
      entry: 'VUKZ Complete ou Club Elite',
    },
    {
      name: 'Federações Estaduais',
      size: '~150 com orçamento tech',
      pain: 'Dados fragmentados, sem visão unificada dos atletas',
      willingness: 'Alta — R$ 5.000-15.000/mês via edital ou orçamento',
      entry: 'Enterprise + white-label',
    },
    {
      name: 'Academias Premium',
      size: '~5.000 com perfil tech-forward',
      pain: 'Correção de forma manual, alta rotatividade',
      willingness: 'Média — R$ 800-2.000/mês se demonstrar retenção de alunos',
      entry: 'Pipeline Academia (2028)',
    },
  ],

  competitivePosition: `A VUKZ ocupa um espaço único no mercado brasileiro: a interseção entre 
visão computacional, agentes de IA e analytics esportivo multi-modal. Competidores internacionais 
(Hudl, Catapult) têm preços 10-50x maiores e não atendem o mercado brasileiro. Competidores locais 
são manuais (planilhas) ou single-sport. Nenhum player combina CV + AI agents + multi-sport + 
governança no mesmo produto.`,

  internationalOpportunity: `LATAM (Argentina, Chile, Colômbia) é o primeiro mercado de expansão 
natural — mesmo idioma/cultura esportiva, mesmo gap tecnológico, possibilidade de operação remota 
com vendedor local. Europa (Portugal, Espanha) é oportunidade de médio prazo via Copilot marketplace.`,
};

export const ROUND_STATUS = {
  current: 'Seed',
  target: 1300000, // R$ 1.3M
  targetUSD: 250000,
  status: 'Preparando materiais — pré-roadshow',
  timeline: 'Q3-Q4 2026',
  idealInvestor: [
    'Fundo seed focado em SaaS/AI no Brasil',
    'Corporate VC de esporte (CBF Ventures, 2TM, Gávea Angels)',
    'Anjos com background em sports tech ou Microsoft ecosystem',
    'Aceleradoras (Endeavor, ACE, Wayra, Microsoft Reactor)',
  ],
  milestones: [
    { item: 'Produto em produção (natação + atletismo)', status: 'done' },
    { item: '35+ AI agents funcionais', status: 'done' },
    { item: 'SwimVision beta', status: 'done' },
    { item: 'TrackVision beta (multi-sport)', status: 'done' },
    { item: 'Microsoft for Startups aprovado ($250K)', status: 'done' },
    { item: 'SaaS Control Plane (billing + quotas)', status: 'done' },
    { item: '5 pilotos pagantes', status: 'in_progress' },
    { item: 'Pitch deck finalizado', status: 'in_progress' },
    { item: 'Data room completo', status: 'in_progress' },
    { item: 'Conversão LTDA → S/A', status: 'pending' },
    { item: 'Roadshow (5-10 fundos)', status: 'pending' },
  ],
};
